import React, { useMemo } from 'react';
import { FaChevronDown, FaChevronUp, FaPlus } from 'react-icons/fa';
import styles from './FilterSidebar.module.css';

const STATUS_OPTIONS = [
  { id: 'active', label: 'Active' },
  { id: 'all', label: 'All' },
  { id: 'inactive', label: 'Inactive' }
];

const COLOR_HEX = {
  neutral: '#9aa3b2',
  brand: '#7c3aed',
  success: '#16a34a',
  info: '#2563eb',
  warning: '#d97706',
  danger: '#dc2626',
  accent: '#14b8a6'
};

function FilterGroup({ title, count, action, children, defaultOpen = true }) {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <div className={styles.group}>
      <button
        type="button"
        className={styles.groupHeader}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={styles.groupChevron}>
          {open ? <FaChevronUp /> : <FaChevronDown />}
        </span>
        <span className={styles.groupTitle}>{title}</span>
        {count != null && count !== '' ? <span className={styles.groupCount}>{count}</span> : null}
        {action ? (
          <span
            className={styles.groupAction}
            onClick={(e) => {
              e.stopPropagation();
              action.onClick();
            }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.stopPropagation();
                action.onClick();
              }
            }}
          >
            {action.icon}
          </span>
        ) : null}
      </button>
      {open ? <div className={styles.groupBody}>{children}</div> : null}
    </div>
  );
}

export default function FilterSidebar({
  status,
  onStatusChange,
  labels = [],
  selectedLabelIds = [],
  onToggleLabel,
  onSelectAllLabels,
  onClearLabels,
  onManageLabels
}) {
  const selectedSet = useMemo(() => new Set(selectedLabelIds), [selectedLabelIds]);

  const grouped = useMemo(() => {
    const groups = new Map();
    for (const l of labels) {
      const c = (l.color || 'neutral').toLowerCase();
      if (!groups.has(c)) groups.set(c, []);
      groups.get(c).push(l);
    }
    // Stable order: brand, success, info, warning, danger, accent, neutral
    const order = ['brand', 'success', 'info', 'warning', 'danger', 'accent', 'neutral'];
    return order
      .filter((k) => groups.has(k))
      .map((k) => ({ color: k, items: groups.get(k) }));
  }, [labels]);

  const totalFamAssignments = labels.reduce((sum, l) => sum + (Number(l.family_count) || 0), 0);
  const totalStudAssignments = labels.reduce((sum, l) => sum + (Number(l.student_count) || 0), 0);
  const assignmentBadge =
    labels.length > 0
      ? `${totalFamAssignments.toLocaleString()} fam · ${totalStudAssignments.toLocaleString()} stud`
      : null;

  return (
    <aside className={styles.sidebar}>
      <header className={styles.header}>
        <h3>Filters</h3>
        <button
          type="button"
          className={styles.resetLink}
          onClick={() => {
            onStatusChange('active');
            onClearLabels && onClearLabels();
          }}
        >
          Reset
        </button>
      </header>

      <div className={styles.statusRow} role="tablist">
        {STATUS_OPTIONS.map((opt) => (
          <button
            key={opt.id}
            type="button"
            role="tab"
            aria-selected={status === opt.id}
            className={[
              styles.statusBtn,
              status === opt.id ? styles.statusBtnActive : ''
            ].join(' ')}
            onClick={() => onStatusChange(opt.id)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <FilterGroup
        title="Labels"
        count={assignmentBadge}
        action={onManageLabels ? { icon: <FaPlus />, onClick: onManageLabels } : null}
      >
        {labels.length === 0 ? (
          <div className={styles.empty}>
            No labels yet.
            {onManageLabels ? (
              <button type="button" className={styles.linkBtn} onClick={onManageLabels}>
                Create one
              </button>
            ) : null}
          </div>
        ) : (
          <>
            {onSelectAllLabels ? (
              <label className={styles.selectAll}>
                <input
                  type="checkbox"
                  checked={
                    selectedLabelIds.length > 0 &&
                    selectedLabelIds.length === labels.length
                  }
                  onChange={(e) => {
                    if (e.target.checked) onSelectAllLabels();
                    else onClearLabels && onClearLabels();
                  }}
                />
                <span>Select all</span>
              </label>
            ) : null}
            {grouped.map((g) => (
              <ul key={g.color} className={styles.list}>
                {g.items.map((l) => {
                  const checked = selectedSet.has(l.id);
                  const fc = Number(l.family_count) || 0;
                  const sc = Number(l.student_count) || 0;
                  return (
                    <li key={l.id}>
                      <label className={styles.item}>
                        <span
                          className={styles.swatch}
                          style={{ background: COLOR_HEX[g.color] || COLOR_HEX.neutral }}
                          aria-hidden="true"
                        />
                        <span className={styles.itemName} title={l.name}>
                          {l.name}
                        </span>
                        <span className={styles.itemCounts}>
                          <span className={styles.itemCountSeg} title="Families with this label">
                            <span className={styles.itemCountKey}>F</span>
                            {fc.toLocaleString()}
                          </span>
                          <span className={styles.itemCountSep}>·</span>
                          <span className={styles.itemCountSeg} title="Students with this label">
                            <span className={styles.itemCountKey}>S</span>
                            {sc.toLocaleString()}
                          </span>
                        </span>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => onToggleLabel(l.id)}
                          className={styles.itemCheck}
                        />
                      </label>
                    </li>
                  );
                })}
              </ul>
            ))}
          </>
        )}
      </FilterGroup>
    </aside>
  );
}
